"use client"

// src/components/TablaGastosComponent.js
import { useState, useEffect, useContext } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Modal,
  Box,
  Typography,
  Button,
  alpha,
  Paper,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material"
import PersonIcon from "@mui/icons-material/Person"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"

import { ErpContext } from "../../context/ErpContext"
import CustomPagination from "./CustomPagination"
import GraficasGastos from "./graficas/GraficasGastos"
import ModalViewTablaEgreso from "./ModalViewTablaEgreso.js"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"

import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from "@mui/icons-material/Visibility";

import { ThemeProvider, createTheme } from "@mui/material/styles"

// Reutilizo los estilos de la tabla de ventas (colores de header, hover, etc.)
import "./TablaVentasComponent.css"

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
}

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#5b5b5f" },
    secondary: { main: "rgb(244, 177, 14)" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 500 } },
    },
  },
})

export default function TablaGastosComponent() {
  const {
    subCategoriaTransaccion,
    getSubCategoriaTransaccion,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    getGastosTransacciones,
    medioDePago,
    getMedioDePago,
    tipoMedioDePago,
    getTipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
    putTransaccion,
    postTransaccion,
    getEntidadByID,
    postCrearPago,
    postTransaccionPago,
    updateCuentaCorriente,
    user,
    updateTransaccionPago,
    getCategoriaTransaccion,
    categoriaTransaccion,
    isNavbarCollapsed,
    updateDeleteTransaccion,
    ubicaciones,
    getUbicacion,
    usuarios,
    getUsuario,
    tipoTransaccion,
    getTipoTransaccion,
  } = useContext(ErpContext)

  // Catálogos
  const categorias = categoriaTransaccion
  const subCategories = subCategoriaTransaccion || []
  const entidades = entidadesClientesTresLetras || []
  const tiposMedios = tipoMedioDePago || []
  const tiposEntidadPermitidos = [1, 2]

  // Estado tabla y filtros
  const today = dayjs()

  const [filtros, setFiltros] = useState({
    startDate: today.subtract(30, "day"),
    endDate: today,
    categoria: null,
    subcategoria: null,
    entidad: null,
    ubicacion: null,
    tipoTransaccion: null,
    usuario: null,
  })
  const [searchTextEntidad, setSearchTextEntidad] = useState("")
  const [searchTextUsuario, setSearchTextUsuario] = useState("")
  const [gastos, setGastos] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const [montoTotalGastos, setMontoTotalGastos] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [totalPages, setTotalPages] = useState(0)

  // Estado modal y formulario
  const [open, setOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState("")
  const [saveButtonText, setSaveButtonText] = useState("")
  const [selected, setSelected] = useState(null)
  const [actionType, setActionType] = useState("") // 'edit' or 'duplicate'
  const [formData, setFormData] = useState({
    descripcion: "",
    montoTotal: "",
    idCategorizacion: null,
    idEntidad: null,
    idTipoMedioPago: null,
    idMedioDePago: null,
    idUbicacion: null,
  })
  const [selectedTipoMedio, setSelectedTipoMedio] = useState(null)
  const [filteredMedios, setFilteredMedios] = useState([])
  const [selectedMedio, setSelectedMedio] = useState(null)
  const [selectedEntidad, setSelectedEntidad] = useState(null)
  const [entidadOptions, setEntidadOptions] = useState([])
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [openGraphModal, setOpenGraphModal] = useState(false)
  
  // Estado para modal de confirmación de eliminación
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  // Estados para modal de visualización de detalle
  const [openModalEgreso, setOpenModalEgreso] = useState(false)
  const [selectedTransaccion, setSelectedTransaccion] = useState(null)

  // Carga catálogos al montar
  useEffect(() => {
    getSubCategoriaTransaccion()
    getCategoriaTransaccion()
    getTipoMedioDePago()
    getMedioDePago()
    getUbicacion()
    getUsuario()
    getTipoTransaccion()
  }, [])

  // Búsqueda entidades filtro de tabla
  useEffect(() => {
    const fetchTablaEntidades = async () => {
      if (searchTextEntidad.length >= 3) {
        await getEntidadesClientesTresLetras(searchTextEntidad, tiposEntidadPermitidos)
      }
    }
    fetchTablaEntidades()
  }, [searchTextEntidad])

  // Pre-carga de entidad al abrir modal
  useEffect(() => {
    const fetchModalEntidad = async () => {
      if (formData.idEntidad) {
        const ent = await getEntidadByID(formData.idEntidad)
        const ops = ent ? [ent] : []
        setEntidadOptions(ops)
        setSelectedEntidad(ent)
      } else {
        setEntidadOptions([])
        setSelectedEntidad(null)
      }
    }
    fetchModalEntidad()
  }, [formData.idEntidad])

  // Carga tabla
  const loadData = async () => {
    setLoading(true)
    try {
      const params = {
        page: page, // back 1-based
        limit: 100,
        categoria: filtros.categoria?.id || null,
        idSubCategoria: filtros.subcategoria?.id || null,
        idEntidad: filtros.entidad?.id || null,
        ubicacionId: filtros.ubicacion?.id || null,
        idTipoTransaccion: filtros.tipoTransaccion?.id || null,
        idUsuario: filtros.usuario?.id || null,
        startDate: filtros.startDate ? filtros.startDate.startOf("day").toISOString() : null,
        endDate: filtros.endDate ? filtros.endDate.endOf("day").toISOString() : null,
      }

      const { data, totalCount, totalMontoGastos, totalPages, currentPage } = await getGastosTransacciones(params)
      
      // Aplano descripciones para columnas visibles
      const filas = data.map((gasto) => {
        const fechaDate = new Date(gasto.fecha);
        return {
          ...gasto,
          fechaTS: isNaN(fechaDate.getTime()) ? 0 : fechaDate.getTime(),
          fecha: new Intl.DateTimeFormat("es-AR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }).format(fechaDate),
          subCategoriaDesc: gasto.subCategoria?.descripcion ?? "",
          categoriaDesc: gasto.subCategoria?.categoria?.descripcion ?? "",
          ubicacionDesc: gasto.ubicacion?.descripcion ?? "",
          tipoTransaccionDesc: gasto.tipoTransaccion?.descripcion ?? "",
          usuarioDesc: gasto.usuario?.usuario ?? "",
        };
      })

      // Orden default (descendente, más nuevas primero)
      filas.sort((a, b) => b.fechaTS - a.fechaTS)

      // console.log("filas", filas)

      setGastos(filas)
      setRowCount(totalCount)
      setMontoTotalGastos(totalMontoGastos || 0)
      setTotalPages(totalPages)
      setPage(currentPage) // mantener paginación como viene del back
    } catch (err) {
      console.error("Error al cargar gastos:", err)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    loadData()
  }, [])

  const handleFilterChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
    setPage(0)
  }

  // Abre modal genérico
  const openModal = ({ row, title, buttonText, type }) => {
    setModalTitle(title)
    setSaveButtonText(buttonText)
    setActionType(type)
    setSelected(row)

    const pago = row.transaccionPago?.[0]?.pago || {}
    const ubicacionActual = row.ubicacion || null
    
    setFormData({
      descripcion: row.descripcion,
      montoTotal: row.montoTotal,
      idCategorizacion: row.idCategorizacion,
      idEntidad: row.idEntidad,
      idTipoMedioPago: pago.medioDePago?.tipoMedioDePago?.id || null,
      idMedioDePago: pago.medioDePago?.id || null,
      idUbicacion: ubicacionActual?.id || null,
    })
    
    setSelectedUbicacion(ubicacionActual)

    setOpen(true)
  }

  // Refresca medios cuando cambia el tipo
  useEffect(() => {
    const fetchMedios = async () => {
      if (formData.idTipoMedioPago) {
        const tipoObj = tiposMedios.find((t) => t.id === formData.idTipoMedioPago) || null
        setSelectedTipoMedio(tipoObj)
        const list = await getMedioDePagoIdTipoEspecifico(tipoObj)
        setFilteredMedios(list || [])
        const foundMedio = (list || []).find((m) => m.id === formData.idMedioDePago) || null
        setSelectedMedio(foundMedio)
      } else {
        setSelectedTipoMedio(null)
        setFilteredMedios([])
        setSelectedMedio(null)
      }
    }
    fetchMedios()
  }, [formData.idTipoMedioPago])

  const buscarFiltros = async () => {
    await loadData()
  }

  const handleEdit = (row) =>
    openModal({
      row,
      title: "Editar Gasto",
      buttonText: "Guardar cambios",
      type: "edit",
    })

  const handleDuplicate = (row) =>
    openModal({
      row,
      title: "Duplicar Gasto",
      buttonText: "Crear duplicado",
      type: "duplicate",
    })

  const handleDelete = (id) => {
    setDeleteId(id)
    setOpenDeleteDialog(true)
  }

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await updateDeleteTransaccion(deleteId)
      await loadData()
      setOpenDeleteDialog(false)
      setDeleteId(null)
    }
  }

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false)
    setDeleteId(null)
  }

  // Función para abrir modal de visualización de detalle
  const handleOpenModal = async (transaccionRow) => {
    try {
      // Preparar los datos de la transacción en el formato esperado por el modal
      // El formato debe coincidir con TablaTransaccionComponent
      const transaccionFormatted = {
        id: transaccionRow.id,
        montoTotal: transaccionRow.montoTotal,
        descripcion: transaccionRow.descripcion,
        idEntidadNumero: transaccionRow.idEntidad,
        descripcionEntidad: transaccionRow.entidad?.descripcion || "",
        apellido: transaccionRow.entidad?.apellido || "",
        dniCuitCuil: transaccionRow.entidad?.dniCuitCuil || "",
        ubicacion: transaccionRow.ubicacion?.descripcion || "",
        usuario: transaccionRow.usuario?.usuario || "",
        fecha: transaccionRow.fecha,
        fechaHoraCreacion: transaccionRow.fechaHoraCreacion || new Date(transaccionRow.fecha),
        createdAt: transaccionRow.fecha, // Misma fecha que la tabla (formateada)
        idTipoTransaccion: transaccionRow.tipoTransaccion?.descripcion || transaccionRow.tipoTransaccionDesc || "",
        negocio: transaccionRow.negocio?.descripcion || "",
        impuestos: transaccionRow.impuestos || [],
        montoDescuento: transaccionRow.montoDescuento || 0,
        porcentajeDescuento: transaccionRow.porcentajeDescuento || 0,
        pagos: (transaccionRow.transaccionPago || []).map(tp => ({
          descripcion: tp.pago?.medioDePago?.descripcion || "",
          monto: tp.pago?.montoTotal || 0,
        })),
        archivosAdjuntos: transaccionRow.archivosAdjuntos || null,
      }
      
      setSelectedTransaccion(transaccionFormatted)
      setOpenModalEgreso(true)
    } catch (error) {
      console.error("Error al abrir el modal:", error)
    }
  }

  const handleCloseModal = () => {
    setOpenModalEgreso(false)
    setSelectedTransaccion(null)
  }

  const handleSave = async () => {
    try {
      if (actionType === "edit") {
        await putTransaccion(
          selected.id,
          formData.montoTotal,
          5,
          formData.idEntidad,
          formData.idCategorizacion,
          formData.descripcion,
          user.id,
          formData.idUbicacion || 1,
          1,
          0,
          0,
        )
        await updateTransaccionPago(selected.id, formData.idMedioDePago, formData.montoTotal)
      } else if (actionType === "duplicate") {
        const transaccionResponse = await postTransaccion(
          formData.montoTotal,
          5,
          formData.idEntidad,
          formData.idCategorizacion,
          formData.descripcion,
          user.id,
          formData.idUbicacion || 1,
          1,
          0,
          0,
        )

        const idTransaccion = transaccionResponse.data.id

        const transaccionCrearPago = await postCrearPago(1, 1, formData.idMedioDePago, formData.montoTotal)
        const idTransaccionCrearPago = transaccionCrearPago.data.id
        await postTransaccionPago(idTransaccion, idTransaccionCrearPago)

        if (formData.aplicarCuentaCorriente) {
          await updateCuentaCorriente(formData.idEntidad, formData.montoTotal, 5)
        }
      }
    } catch (err) {
      console.error("Error al guardar:", err)
    }
    setOpen(false)
    await loadData()
  }

  // ==== Columnas con UI replicada de TablaVentas (solo diseño) ====
  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.3,
      minWidth: 70,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "fecha",
      headerName: "Fecha",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      sortable: false, // <--- clave: no mostrar control de orden
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      type: "number",
      flex: 0.8,
      minWidth: 130,
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          icon={<AttachMoneyIcon fontSize="small" />}
          label={params.value?.toLocaleString("es-AR") || "0.00"}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha("#4CAF50", 0.1), // mismo chip/estilo que ventas
            color: "#4CAF50",
            "& .MuiChip-icon": { color: "#4CAF50" },
            display: "flex",
          }}
        />
      ),
    },
    {
      field: "entidad",
      headerName: "Entidad",
      flex: 1.2,
      minWidth: 180,
      headerAlign: "center",
      renderCell: (params) => {
        const label = params.row.entidad?.descripcion || ""
        return (
          <Tooltip title={label}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
              <Typography variant="body2" noWrap fontWeight={500}>
                {label}
              </Typography>
            </Box>
          </Tooltip>
        )
      },
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      flex: 1.6,
      minWidth: 260,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "flex-start", boxSizing: "border-box", padding: "10px"}}>
          <Typography
            variant="body1"        // un toque más grande que body2
            fontWeight={600}       // resalta un poco más
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,  // hasta 2 líneas
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
              lineHeight: 1,
              color: "rgba(0,0,0,0.85)", // mejora el contraste sin cambiar la paleta
            }}
            title={params.value || ""} // tooltip nativo del browser
          >
            {params.value || ""}
          </Typography>
        </Box>
      ),
    },
    { field: "categoriaDesc", headerName: "Categoría", flex: 0.9, minWidth: 180, headerAlign: "center" },
    { field: "subCategoriaDesc", headerName: "Subcategoría", flex: 0.9, minWidth: 180, headerAlign: "center" },
    {
      field: "ubicacionDesc",
      headerName: "Ubicación",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2" fontWeight={500}>
            {params.value || "Sin ubicación"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "tipoTransaccionDesc",
      headerName: "Tipo Transacción",
      flex: 1,
      minWidth: 140,
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "usuarioDesc",
      headerName: "Usuario", 
      flex: 1, 
      minWidth: 150, 
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2" fontWeight={500}>
            {params.value || "Sin usuario"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 0.5,
      minWidth: 150,
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Ver detalles">
            <IconButton
              className="colorGeneralParaIconos"
              onClick={() => handleOpenModal(row)}
              size="small"
              sx={{
                bgcolor: alpha("#5b5b5f", 0.1),
                "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar gasto">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleEdit(row)}
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Duplicar gasto">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleDuplicate(row)}
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Eliminar gasto">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleDelete(row.id)}
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Filtros */}
        <Paper
          className="diseñoFiltroTransaccionesVenta"
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            flexShrink: 0,
          }}
        >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => {
                  if (!filtros.startDate) return
                  handleFilterChange("startDate", filtros.startDate.subtract(1, "day"))
                }}
                color="primary"
                size="small"
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>

              <DatePicker
                label="Desde"
                value={filtros.startDate}
                onChange={(newValue) => handleFilterChange("startDate", newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                sx={{ width: 150 }}
              />

              <DatePicker
                label="Hasta"
                value={filtros.endDate}
                onChange={(newValue) => handleFilterChange("endDate", newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                sx={{ width: 150 }}
              />

              <IconButton
                onClick={() => {
                  if (!filtros.startDate) return
                  handleFilterChange("startDate", filtros.startDate.add(1, "day"))
                }}
                color="primary"
                size="small"
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </LocalizationProvider>

          <Autocomplete
            size="small"
            options={categorias}
            getOptionLabel={(opt) => opt.descripcion || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.categoria}
            onChange={(_, v) => handleFilterChange("categoria", v)}
            renderInput={(params) => <TextField {...params} label="Categoria" size="small" />}
            sx={{ width: "12%" }}
          />

          <Autocomplete
            size="small"
            options={subCategories}
            getOptionLabel={(opt) => opt.descripcion || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.subcategoria}
            onChange={(_, v) => handleFilterChange("subcategoria", v)}
            renderInput={(params) => <TextField {...params} label="Subcategoría" size="small" />}
            sx={{ width: "12%" }}
          />

          <Autocomplete
            size="small"
            options={entidades.map((it) => ({
              ...it,
              label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
            }))}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.entidad}
            onChange={(_, v) => handleFilterChange("entidad", v)}
            onInputChange={(_, txt) => setSearchTextEntidad(txt)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Buscar Entidad..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ width: "12%" }}
          />

          <Autocomplete
            size="small"
            options={ubicaciones}
            getOptionLabel={(opt) => opt.descripcion || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.ubicacion}
            onChange={(_, v) => handleFilterChange("ubicacion", v)}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder="Ubicación"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <LocationOnIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ width: "12%" }}
          />

          <Autocomplete
            size="small"
            options={(tipoTransaccion || []).filter((t) => [5, 9].includes(t.id))}
            getOptionLabel={(opt) => opt.descripcion || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.tipoTransaccion}
            onChange={(_, v) => handleFilterChange("tipoTransaccion", v)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Tipo Transacción"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SwapHorizIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ width: "12%" }}
          />

          <Autocomplete
            size="small"
            options={usuarios || []}
            getOptionLabel={(opt) => opt.usuario || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            value={filtros.usuario}
            onChange={(_, v) => handleFilterChange("usuario", v)}
            onInputChange={(_, txt) => setSearchTextUsuario(txt)}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const searchLower = inputValue.toLowerCase();
              return options.filter((opt) =>
                opt.usuario?.toLowerCase().includes(searchLower)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Buscar Usuario..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ width: "12%" }}
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={buscarFiltros}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: "#5b5b5f",
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Buscar
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenGraphModal(true)}
              sx={{
                backgroundColor: theme.palette.secondary.main,
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha(theme.palette.secondary.main, 0.8) },
              }}
            >
              Gráficas
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabla */}
      <Paper
        sx={{
          flex: 1,
          m: 1,
          borderRadius: 3,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          position: "relative",
          overflow: "hidden",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DataGrid
          key={isNavbarCollapsed ? "collapsed" : "expanded"}
          rows={gastos}
          className="tablaVentasDataGrid"
          columns={columns}
          getRowId={(row) => row.id}
          rowCount={totalPages * 100}
          pagination
          paginationMode="server"
          getRowHeight={() => 35}
          headerHeight={35}
          sx={{
            width: "100%",
            border: 0,
            "& .MuiDataGrid-footerContainer": { display: "none !important" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#5b5b5f",
              color: "white",
              borderBottom: "2px solid #333",
              height: "45px !important",
              minHeight: "45px !important",
              maxHeight: "45px !important",
            },
            "& .MuiDataGrid-columnHeader": {
              height: "45px !important",
              minHeight: "45px !important",
              maxHeight: "45px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "white !important",
              lineHeight: "45px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
              color: "white !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiInputBase-root": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiTablePagination-root": {
              color: "black !important",
            },
            "& .MuiSelect-icon": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            "& .MuiDataGrid-row": {
              "&:nth-of-type(even)": { backgroundColor: "#F7F7F7" },
              "&:hover": {
                backgroundColor: "#E3EAFD !important",
                transition: "background 0.3s ease",
              },
            },
            "& .row-devolucion": {
              backgroundColor: alpha("#9e9e9e !important", 0.05),
              "&:hover": { backgroundColor: alpha("#9e9e9e !important", 0.1) },
            },
            "& .MuiDataGrid-virtualScroller": { lineHeight: "none !important" },
            "& .MuiDataGrid-footerContainer": {
              position: "sticky",
              bottom: 0,
              backgroundColor: "#ffffff",
              borderTop: "1px solid #ddd",
              zIndex: 10,
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": { width: "2px" },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
            },
          }}
          components={{ Pagination: () => null }}
        />
      </Paper>

      {/* Contenedor del footer (paginación) */}
      <Box sx={{ flexShrink: 0, p: 1, backgroundColor: "white", position: "relative" }}>
        <CustomPagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={rowCount}
          montoTotalTransacciones={montoTotalGastos}
          vieneDe={"tablaGastos"}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </Box>

      {/* Modal edición/duplicado */}
      <Modal open={open} onClose={() => setOpen(false)} aria-labelledby="modal-title">
        <Box sx={style}>
          <Typography id="modal-title" variant="h6" gutterBottom>
            {modalTitle}
          </Typography>
          <Box component="form" noValidate autoComplete="off" sx={{ display: "grid", gap: 2 }}>
            <Autocomplete
              size="small"
              options={ubicaciones}
              getOptionLabel={(opt) => opt.descripcion || ""}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              value={selectedUbicacion}
              onChange={(_, v) => {
                setSelectedUbicacion(v)
                setFormData((prev) => ({ ...prev, idUbicacion: v?.id || null }))
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ubicación"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocationOnIcon color="action" fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              label="Descripción"
              value={formData.descripcion}
              onChange={(e) => setFormData((prev) => ({ ...prev, descripcion: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Monto Total"
              type="number"
              value={formData.montoTotal}
              onChange={(e) => setFormData((prev) => ({ ...prev, montoTotal: e.target.value }))}
              fullWidth
            />
            <Autocomplete
              size="small"
              options={subCategories}
              getOptionLabel={(opt) => opt.descripcion || ""}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              value={subCategories.find((s) => s.id === formData.idCategorizacion) || null}
              onChange={(_, v) => setFormData((prev) => ({ ...prev, idCategorizacion: v?.id || null }))}
              renderInput={(params) => <TextField {...params} label="Subcategoría" size="small" />}
            />
            <Autocomplete
              size="small"
              options={entidadOptions}
              getOptionLabel={(opt) => `${opt.descripcion} - ${opt.apellido} - ${opt.dniCuitCuil}`}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              value={selectedEntidad}
              onChange={async (_, v) => {
                setSelectedEntidad(v)
                setFormData((prev) => ({ ...prev, idEntidad: v?.id || null }))
              }}
              onInputChange={async (_, txt, reason) =>
                reason === "input" && setEntidadOptions(
                  txt.length >= 3 ? await getEntidadesClientesTresLetras(txt, tiposEntidadPermitidos) : []
                )
              }
              renderInput={(params) => <TextField {...params} label="Entidad" size="small" />}
            />
            <Autocomplete
              size="small"
              options={tiposMedios}
              getOptionLabel={(opt) => opt.descripcion}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              value={selectedTipoMedio}
              onChange={(_, v) => {
                setSelectedTipoMedio(v)
                setSelectedMedio(null)
                setFormData((prev) => ({
                  ...prev,
                  idTipoMedioPago: v?.id ?? null,
                  idMedioDePago: null,
                }))
              }}
              renderInput={(params) => <TextField {...params} label="Tipo Medio de Pago" size="small" />}
            />
            <Autocomplete
              size="small"
              options={filteredMedios}
              getOptionLabel={(opt) => opt.descripcion}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              value={selectedMedio}
              onChange={(_, v) => {
                setSelectedMedio(v)
                setFormData((prev) => ({ ...prev, idMedioDePago: v?.id ?? null }))
              }}
              renderInput={(params) => <TextField {...params} label="Medio de Pago" size="small" />}
            />
          </Box>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={() => setOpen(false)} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSave}>
              {saveButtonText}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de Gráficas */}
      <Modal open={openGraphModal} onClose={() => setOpenGraphModal(false)} aria-labelledby="graph-modal-title">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            maxWidth: 1200,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Typography id="graph-modal-title" variant="h6" gutterBottom>
            Análisis de Gastos
          </Typography>

          <GraficasGastos gastos={gastos} subCategories={subCategories} filtros={filtros} />

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button onClick={() => setOpenGraphModal(false)} variant="contained">
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirmar eliminación
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            ¿Estás seguro de que deseas eliminar este gasto? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de visualización de detalle */}
      {openModalEgreso && (
        <ModalViewTablaEgreso
          openModal={openModalEgreso}
          handleCloseModal={handleCloseModal}
          selectedTransaccion={selectedTransaccion}
        />
      )}
      </ThemeProvider>
    </div>
  )
}
